import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getWaysListSelector, getIsWaysListLoadingSelector, getPaginationSelector } from './selectors';
import { getWaysListAction, resetStateAction } from './actions';
import { getIsAvailableWaysButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const WaysContainerWrapper = ({ onGetWaysList, waysList, isWaysListLoading, pagination, isAvailable }) => {
	useInjectSaga({ key: 'waysContainerSaga', saga });
	useInjectReducer({ key: 'waysContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetWaysList({ searchQuery: removeModalQuery(query.toString()) });
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'way');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetWaysList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		onGetWaysList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetWaysList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetWaysList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<>
			<DataGridComponent
				headerText="Маршруты"
				onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
				columns={TABLE_COLUMNS()}
				rows={waysList}
				pagination={
					!pagination
						? null
						: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
				}
				isTableDataLoading={isWaysListLoading}
				handleSortChange={handleSortChange}
				handleFilterChange={handleFilterChange}
			/>
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	waysList: getWaysListSelector(),
	isWaysListLoading: getIsWaysListLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableWaysButtonsSelector(),
});

const mapDispatchToProps = {
	onGetWaysList: getWaysListAction,
	onResetState: resetStateAction,
};

const WaysContainer = connect(mapStateToProps, mapDispatchToProps)(WaysContainerWrapper);

WaysContainerWrapper.propTypes = {
	onGetWaysList: PropTypes.func.isRequired,
	waysList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
			address_from: PropTypes.string.isRequired,
			address_to: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	isWaysListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default WaysContainer;
